/**
 * 三 module 通用版本管理面板 · S2 Day 8b
 *
 * 嵌入点：M02 L2 编辑器（l2_rules）· M03 playbook editor（playbook/{pb_id}）· M03 intent-mapping（intent_mapping）
 * 功能：
 *   - 保存当前快照（输入 message + author 自动）
 *   - 历史抽屉（list 时间倒序）
 *   - Restore 二次确认（破坏性 · 必须 Dialog · X3 §10.2）
 *
 * Token 命名空间：surface-{card|border|subtle} / text-{primary|secondary|muted} / brand-{accent|danger|success|warning}
 */
import { AlertTriangle, Camera, History, RotateCcw } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	type SnapshotMeta,
	type SnapshotModule,
	useListSnapshots,
	useRestoreSnapshot,
	useSaveSnapshot,
} from "@/lib/api/snapshot";

interface Props {
	module: SnapshotModule;
	/** 显示用 module 名（中文） */
	moduleLabel: string;
	/** UI 角色限定 testid 前缀（e.g. "l2-snapshot" / "pb-snapshot-PB02"） */
	testidPrefix: string;
}

function formatTimestamp(iso: string): string {
	try {
		const d = new Date(iso);
		const pad = (n: number) => n.toString().padStart(2, "0");
		return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
	} catch {
		return iso;
	}
}

export function SnapshotPanel({ module, moduleLabel, testidPrefix }: Props) {
	const [historyOpen, setHistoryOpen] = useState(false);
	const [saveOpen, setSaveOpen] = useState(false);
	const [confirmRestore, setConfirmRestore] = useState<SnapshotMeta | null>(null);
	const [author, setAuthor] = useState("admin@local");
	const [message, setMessage] = useState("");

	const list = useListSnapshots(module, historyOpen);
	const save = useSaveSnapshot(module);
	const restore = useRestoreSnapshot(module);

	const handleSave = () => {
		if (!message.trim()) return;
		save.mutate(
			{ author: author.trim() || "anon", message: message.trim() },
			{
				onSuccess: () => {
					setSaveOpen(false);
					setMessage("");
					setHistoryOpen(true);
				},
			},
		);
	};

	const handleRestore = (meta: SnapshotMeta) => {
		restore.mutate(
			{ snapshot_id: meta.snapshot_id },
			{
				onSuccess: () => {
					setConfirmRestore(null);
					setHistoryOpen(false);
				},
			},
		);
	};

	return (
		<div
			className="flex flex-wrap items-center gap-2 rounded-md border border-surface-border bg-surface-subtle px-3 py-2"
			data-testid={`${testidPrefix}-bar`}
		>
			<span className="text-xs font-medium text-text-secondary">版本管理</span>
			<span className="text-xs text-text-muted">·</span>
			<Button
				type="button"
				variant="outline"
				size="sm"
				data-testid={`${testidPrefix}-save-button`}
				onClick={() => setSaveOpen(true)}
			>
				<Camera className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
				保存快照
			</Button>
			<Button
				type="button"
				variant="ghost"
				size="sm"
				data-testid={`${testidPrefix}-history-button`}
				onClick={() => setHistoryOpen(true)}
			>
				<History className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
				查看历史
			</Button>

			{/* Save Dialog */}
			<Dialog open={saveOpen} onOpenChange={setSaveOpen}>
				<DialogContent data-testid={`${testidPrefix}-save-dialog`}>
					<DialogHeader>
						<DialogTitle>保存当前 {moduleLabel} 快照？</DialogTitle>
						<DialogDescription>
							快照将写入 apps/api/snapshots/ 文件系统 · 后续可在历史中回滚到此版本。
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-3">
						<label className="block text-sm">
							<span className="text-text-secondary">作者</span>
							<input
								type="text"
								value={author}
								onChange={(e) => setAuthor(e.target.value)}
								className="mt-1 w-full rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
								data-testid={`${testidPrefix}-save-author`}
							/>
						</label>
						<label className="block text-sm">
							<span className="text-text-secondary">说明</span>
							<input
								type="text"
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								placeholder="例如：M02 上线前 baseline / 改前测试 v1"
								className="mt-1 w-full rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
								data-testid={`${testidPrefix}-save-message`}
								required
							/>
						</label>
						{save.isError ? (
							<p className="text-xs text-brand-danger" data-testid={`${testidPrefix}-save-error`}>
								保存失败：{save.error instanceof Error ? save.error.message : String(save.error)}
							</p>
						) : null}
					</div>
					<DialogFooter>
						<DialogClose asChild>
							<Button variant="outline" size="sm" type="button">
								取消
							</Button>
						</DialogClose>
						<Button
							type="button"
							variant="accent"
							size="sm"
							onClick={handleSave}
							disabled={!message.trim() || save.isPending}
							data-testid={`${testidPrefix}-save-confirm`}
						>
							{save.isPending ? "保存中…" : "确认保存"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* History Dialog（抽屉式 · MVP 用 Dialog 居中实现） */}
			<Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
				<DialogContent className="max-w-2xl" data-testid={`${testidPrefix}-history-dialog`}>
					<DialogHeader>
						<DialogTitle>{moduleLabel} 快照历史</DialogTitle>
						<DialogDescription>
							时间倒序 · 点击「回滚」会用快照内容覆盖当前 live 配置
						</DialogDescription>
					</DialogHeader>
					<div className="max-h-[50vh] overflow-y-auto">
						{list.isLoading ? (
							<p className="p-4 text-sm text-text-secondary">加载中…</p>
						) : list.isError ? (
							<p className="p-4 text-sm text-brand-danger">
								加载失败：{list.error instanceof Error ? list.error.message : String(list.error)}
							</p>
						) : (list.data?.items.length ?? 0) === 0 ? (
							<p
								className="p-4 text-sm text-text-muted"
								data-testid={`${testidPrefix}-history-empty`}
							>
								暂无历史快照 · 点上方「保存快照」创建第一个
							</p>
						) : (
							<ul className="divide-y divide-surface-border">
								{list.data?.items.map((m) => (
									<li
										key={m.snapshot_id}
										className="flex items-start justify-between gap-3 py-3"
										data-testid={`${testidPrefix}-history-item-${m.snapshot_id}`}
									>
										<div className="min-w-0 flex-1">
											<div className="text-sm font-medium text-text-primary">{m.message}</div>
											<div className="mt-0.5 text-xs text-text-muted">
												{formatTimestamp(m.timestamp)} · {m.author} · {m.size_bytes} bytes
											</div>
											<div className="mt-0.5 truncate font-mono text-xs text-text-muted">
												{m.snapshot_id}
											</div>
										</div>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={() => setConfirmRestore(m)}
											data-testid={`${testidPrefix}-restore-button-${m.snapshot_id}`}
										>
											<RotateCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
											回滚
										</Button>
									</li>
								))}
							</ul>
						)}
					</div>
				</DialogContent>
			</Dialog>

			{/* Restore 二次确认 Dialog（破坏性 · 红按钮 + ⚠ icon · X3 §10.2） */}
			<Dialog
				open={confirmRestore !== null}
				onOpenChange={(open) => !open && setConfirmRestore(null)}
			>
				<DialogContent data-testid={`${testidPrefix}-restore-confirm-dialog`}>
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<AlertTriangle className="h-5 w-5 text-brand-danger" aria-hidden="true" />
							确认回滚 {moduleLabel} 到此快照？
						</DialogTitle>
						<DialogDescription>
							当前 live 配置将被快照内容覆盖 · 操作不可逆 · 当前未保存的更改将丢失
						</DialogDescription>
					</DialogHeader>
					{confirmRestore ? (
						<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-xs">
							<div className="font-medium text-text-primary">{confirmRestore.message}</div>
							<div className="mt-1 text-text-muted">
								{formatTimestamp(confirmRestore.timestamp)} · {confirmRestore.author}
							</div>
							<div className="mt-1 truncate font-mono text-text-muted">
								{confirmRestore.snapshot_id}
							</div>
						</div>
					) : null}
					{restore.isError ? (
						<p className="text-xs text-brand-danger" data-testid={`${testidPrefix}-restore-error`}>
							回滚失败：
							{restore.error instanceof Error ? restore.error.message : String(restore.error)}
						</p>
					) : null}
					<DialogFooter>
						<DialogClose asChild>
							<Button
								variant="outline"
								size="sm"
								type="button"
								data-testid={`${testidPrefix}-restore-cancel`}
							>
								取消
							</Button>
						</DialogClose>
						<Button
							type="button"
							variant="danger"
							size="sm"
							onClick={() => confirmRestore && handleRestore(confirmRestore)}
							disabled={restore.isPending}
							data-testid={`${testidPrefix}-restore-confirm`}
						>
							{restore.isPending ? "回滚中…" : "确认回滚"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
